/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const SearchTypeaheadSkeleton = ({marginTop, styles}) => (
  <Box data-testid="search-typeahead-loading" mt={marginTop}>
    <Box {...styles.section}>
      <Skeleton height={{base: '10px', md: '12px'}} width="140px" />
      <Box {...styles.list}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={{base: '80px', md: '75px'}} width="full" />
        ))}
      </Box>
    </Box>
  </Box>
);

SearchTypeaheadSkeleton.propTypes = {
  marginTop: PropTypes.number,
  styles: PropTypes.object.isRequired,
};

/**
 * Search typeahead: loading skeleton, results heading + rows, empty state, optional View all.
 */
const SearchTypeaheadPanel = ({
  liveQueryTrimmed,
  debouncedQueryTrimmed,
  minQueryLength,
  isPanelLoading,
  hasDisplayableResults,
  displayRows,
  marginTop = '32px',
  viewAllProductTotal,
  visibleProductRowCount,
  onViewAllResults,
}) => {
  const styles = useMultiStyleConfig('SearchTypeaheadPanel');

  const showViewAll = useMemo(() => {
    if (displayRows.length < 1) {
      return false;
    }

    if (typeof viewAllProductTotal !== 'number' || viewAllProductTotal < 1) {
      return false;
    }

    return viewAllProductTotal > visibleProductRowCount;
  }, [displayRows.length, viewAllProductTotal, visibleProductRowCount]);

  if (!liveQueryTrimmed || liveQueryTrimmed.length < minQueryLength) {
    return null;
  }

  if (isPanelLoading) {
    return <SearchTypeaheadSkeleton marginTop={marginTop} styles={styles} />;
  }

  const settled =
    debouncedQueryTrimmed === liveQueryTrimmed &&
    debouncedQueryTrimmed.length >= minQueryLength;

  if (!settled) {
    return (
      <Box data-testid="search-typeahead-debouncing" mt={marginTop}>
        <Box {...styles.section}>
          <Skeleton height={{base: '10px', md: '12px'}} width="140px" />
          <Box {...styles.list}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={{base: '80px', md: '75px'}} width="full" />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  if (!hasDisplayableResults) {
    return (
      <Box data-testid="search-typeahead-empty" mt={{base: '24px', md: '32px'}}>
        <Text color="brandColors.primaryText" fontSize={{base: 'md', md: 'lg'}}>
          <FormattedMessage
            defaultMessage="Sorry, no matches were found for {quotedQuery}"
            id="search.results.none.line1"
            values={{
              quotedQuery: `'${debouncedQueryTrimmed}'`,
            }}
          />
        </Text>
        <Text color="brandColors.primaryText" fontSize={{base: 'md', md: 'lg'}} mt={2}>
          <FormattedMessage
            defaultMessage="Please try a new search or use suggestions."
            id="search.results.none.line2"
          />
        </Text>
      </Box>
    );
  }

  return (
    <Box data-testid="search-typeahead-results" mt={marginTop} {...styles.wrap}>
      <Box {...styles.section}>
        <Text {...styles.heading}>
          <FormattedMessage
            defaultMessage="Search results"
            id="search.results.heading"
          />
        </Text>
        <Box {...styles.list}>
          {displayRows.map((row) => (
            <Button
              key={row.key}
              variant="unstyled"
              {...styles.item}
              onMouseDown={() => row.onSelect()}
            >
              <Flex {...styles.itemInner}>
                <Box {...styles.itemImage}>
                  {row.imageUrl ? (
                    <Image
                      alt=""
                      h="48px"
                      objectFit="cover"
                      src={row.imageUrl}
                      w="108px"
                    />
                  ) : null}
                </Box>
                <Flex {...styles.itemInfo}>
                  <Text {...styles.itemTitle}>
                    <span dangerouslySetInnerHTML={{__html: row.titleHtml}} />
                  </Text>
                  <Text {...styles.itemTag} noOfLines={1}>
                    {row.tag}
                  </Text>
                </Flex>
              </Flex>
            </Button>
          ))}
        </Box>
      </Box>
      {showViewAll ? (
        <Text
          as="span"
          role="button"
          tabIndex={0}
          {...styles.viewAll}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onViewAllResults(e);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            onViewAllResults(e);
          }}
        >
          <FormattedMessage
            defaultMessage="View all {count} results"
            id="search.results.view_all_count"
            values={{count: viewAllProductTotal}}
          />
        </Text>
      ) : null}
    </Box>
  );
};

SearchTypeaheadPanel.propTypes = {
  liveQueryTrimmed: PropTypes.string.isRequired,
  debouncedQueryTrimmed: PropTypes.string.isRequired,
  minQueryLength: PropTypes.number.isRequired,
  isPanelLoading: PropTypes.bool.isRequired,
  hasDisplayableResults: PropTypes.bool.isRequired,
  marginTop: PropTypes.number,
  displayRows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      kind: PropTypes.oneOf(['product', 'article', 'blog']).isRequired,
      titleHtml: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      onSelect: PropTypes.func.isRequired,
    })
  ).isRequired,
  viewAllProductTotal: PropTypes.number,
  visibleProductRowCount: PropTypes.number.isRequired,
  onViewAllResults: PropTypes.func.isRequired,
};

export default SearchTypeaheadPanel;
